import { MenuItem, Select as MuiSelect, TextFieldProps } from '@mui/material';
import { makeStyles } from '@pmp/theme';
import { useIntlToString } from '@shared/hooks/intl';
import ChevronDownIcon from '@ui/Icon/library/ChevronDownIcon';
import { IconProps } from '@ui/Icon/types';
import { ComponentProps, ReactNode, type ReactElement } from 'react';
import { FormattedMessage } from 'react-intl';
import { isElement } from 'react-is';
import FormLabel, { FormLabelProps } from '../FormLabel';

const useStyles = makeStyles(({ spacing, palette }) => ({
  root: {
    minHeight: spacing(11),
    '& .MuiSelect-icon, & .MuiNativeSelect-icon': {
      right: spacing(3),
    },
  },
  placeholder: {
    color: palette.neutrals[600],
  },
  selectWrapper: {
    display: 'grid',
    columnGap: spacing(4),
    gridTemplateColumns: '1fr',
    width: '100%',
  },
  rightAdditionInput: {
    gridTemplateColumns: '1fr auto',
  },
}));

type Option = ComponentProps<typeof MenuItem> & {
  label: string | ReactElement<{ id: string; values?: any }>;
};

const NativeOptions = ({ options }: { options: readonly Option[] }) => {
  const intlToString = useIntlToString();
  return (
    <>
      {options.map(({ label, ...optionRest }, index) => (
        <option key={index} value={optionRest.value} disabled={optionRest.disabled}>
          {isElement(label) ? intlToString(label) : label}
        </option>
      ))}
    </>
  );
};

type Props = Pick<
  TextFieldProps,
  | 'id'
  | 'children'
  | 'value'
  | 'disabled'
  | 'onClick'
  | 'onChange'
  | 'onBlur'
  | 'onKeyDown'
  | 'SelectProps'
  | 'placeholder'
  | 'fullWidth'
> & {
  options: readonly Option[];
  native?: boolean;
  fieldClassName?: string;
  IconProps?: IconProps;
  showEmpty?: boolean;
  emptyLabel?: ReactNode;
  rightAddition?: ReactNode;
} & FormLabelProps;

const Select = ({
  label,
  helperText,
  descriptionText,
  inverse,
  options,
  SelectProps,
  children,
  native = true,
  className,
  fieldClassName,
  IconProps,
  showEmpty,
  rightAddition,
  emptyLabel,
  ...rest
}: Props) => {
  const { classes, cx } = useStyles();

  const isEmptyValue = !rest.value;
  return (
    <FormLabel
      name={rest.name}
      error={rest.error}
      label={label}
      helperText={helperText}
      descriptionText={descriptionText}
      inverse={inverse}
      className={className}
      required={rest.required}
    >
      <div className={cx(classes.selectWrapper, !!rightAddition && classes.rightAdditionInput)}>
        <MuiSelect
          {...(rest as any)}
          variant="filled"
          id={rest.id || rest.name}
          className={cx(classes.root, isEmptyValue && classes.placeholder, fieldClassName)}
          native={native}
          {...SelectProps}
          IconComponent={(props) => <ChevronDownIcon {...props} {...IconProps} />}
        >
          {showEmpty && native && (
            <option disabled selected value="">
              {emptyLabel || <FormattedMessage id="common.select_an_option" />}
            </option>
          )}
          {native && <NativeOptions options={options} />}
          {!native &&
            options.map(({ label, ...optionRest }, index) => (
              <MenuItem key={index} {...optionRest}>
                {label}
              </MenuItem>
            ))}
          {!native && children}
        </MuiSelect>
        {rightAddition ? rightAddition : null}
      </div>
    </FormLabel>
  );
};

export default Select;
